'use client';

import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { Button } from '@zealot/ui/components/button';
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@zealot/ui/components/input-group';
import { cn } from '@zealot/ui/lib/utils';
import { CheckIcon, ChevronDownIcon, Loader2, XIcon } from 'lucide-react';
import * as React from 'react';

const Combobox = ComboboxPrimitive.Root;

const useComboboxFilter = ComboboxPrimitive.useFilter;

const ComboboxValue = ({ ...props }: ComboboxPrimitive.Value.Props) => {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />;
};

const ComboboxTrigger = ({ className, children, ...props }: ComboboxPrimitive.Trigger.Props) => {
  return (
    <ComboboxPrimitive.Trigger
      data-slot="combobox-trigger"
      className={cn("[&_svg:not([class*='size-'])]:size-3.5", className)}
      {...props}
    >
      {children}
      <ChevronDownIcon className="pointer-events-none size-3.5 text-muted-foreground" />
    </ComboboxPrimitive.Trigger>
  );
};

// gloss
const ComboboxClear = ({
  className,
  'aria-label': ariaLabel = 'Clear selection',
  ...props
}: ComboboxPrimitive.Clear.Props) => {
  return (
    <ComboboxPrimitive.Clear
      data-slot="combobox-clear"
      aria-label={ariaLabel}
      render={<InputGroupButton variant="ghost" size="icon-xs" />}
      className={cn(className)}
      {...props}
    >
      <XIcon className="pointer-events-none" />
    </ComboboxPrimitive.Clear>
  );
};

// gloss
const COMBOBOX_INPUT_SIZES: Record<'sm' | 'default' | 'lg', string> = {
  sm: 'h-7 text-xs',
  default: '',
  lg: 'h-10',
};

// gloss
const ComboboxInput = ({
  className,
  children,
  disabled = false,
  showTrigger = true,
  showClear = false,
  leading,
  size = 'default',
  ...props
}: Omit<ComboboxPrimitive.Input.Props, 'size'> & {
  showTrigger?: boolean;
  showClear?: boolean;
  leading?: React.ReactNode;
  size?: 'sm' | 'default' | 'lg';
}) => {
  return (
    <InputGroup className={cn('w-auto', COMBOBOX_INPUT_SIZES[size], className)}>
      {leading ? <InputGroupAddon align="inline-start">{leading}</InputGroupAddon> : null}
      <ComboboxPrimitive.Input render={<InputGroupInput disabled={disabled} />} {...props} />
      <InputGroupAddon align="inline-end">
        {showTrigger && (
          <InputGroupButton
            size="icon-xs"
            variant="ghost"
            render={<ComboboxTrigger />}
            data-slot="input-group-button"
            className="group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent"
            disabled={disabled}
          />
        )}
        {showClear && <ComboboxClear disabled={disabled} />}
      </InputGroupAddon>
      {children}
    </InputGroup>
  );
};

const ComboboxContent = ({
  className,
  side = 'bottom',
  sideOffset = 6,
  align = 'start',
  alignOffset = 0,
  anchor,
  ...props
}: ComboboxPrimitive.Popup.Props &
  Pick<ComboboxPrimitive.Positioner.Props, 'side' | 'align' | 'sideOffset' | 'alignOffset' | 'anchor'>) => {
  return (
    <ComboboxPrimitive.Portal>
      <ComboboxPrimitive.Positioner
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        className="isolate z-50"
      >
        <ComboboxPrimitive.Popup
          data-slot="combobox-content"
          data-chips={!!anchor}
          className={cn(
            'rounded-lg border bg-popover text-popover-foreground shadow-lg outline-none',
            'origin-[var(--transform-origin)] overflow-hidden',
            'data-open:fade-in-0 data-open:zoom-in-95 data-open:animate-in',
            'data-closed:fade-out-0 data-closed:zoom-out-95 data-closed:animate-out',
            'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
            'group/combobox-content relative max-h-(--available-height)',
            'w-(--anchor-width) max-w-(--available-width)',
            'min-w-[calc(var(--anchor-width)+1.75rem)]',
            'data-[chips=true]:min-w-(--anchor-width)',
            className,
          )}
          {...props}
        />
      </ComboboxPrimitive.Positioner>
    </ComboboxPrimitive.Portal>
  );
};

// gloss
const ComboboxInlineContent = ({ className, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="combobox-content"
      className={cn(
        'group/combobox-content relative mt-1.5 flex min-h-0 flex-1 flex-col',
        'overflow-hidden rounded-lg border bg-popover text-popover-foreground',
        '[&_[data-slot=combobox-list]]:max-h-none [&_[data-slot=combobox-list]]:min-h-0 [&_[data-slot=combobox-list]]:flex-1',
        '[&_[data-slot=combobox-list]]:scrollbar-inset',
        className,
      )}
      {...props}
    />
  );
};

// gloss
const ComboboxContentSlot = ({
  inline = false,
  anchor,
  children,
}: {
  inline?: boolean;
  anchor?: ComboboxPrimitive.Positioner.Props['anchor'];
  children: React.ReactNode;
}) => {
  if (inline) {
    return <ComboboxInlineContent>{children}</ComboboxInlineContent>;
  }
  return <ComboboxContent anchor={anchor}>{children}</ComboboxContent>;
};

const ComboboxList = ({ className, ...props }: ComboboxPrimitive.List.Props) => {
  return (
    <ComboboxPrimitive.List
      data-slot="combobox-list"
      className={cn(
        'max-h-[min(18rem,calc(var(--available-height)-2.25rem))] scroll-py-1 overflow-y-auto overscroll-contain p-1 data-empty:p-0',
        className,
      )}
      {...props}
    />
  );
};

const ComboboxItem = ({ className, children, ...props }: ComboboxPrimitive.Item.Props) => {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        'relative flex w-full cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 outline-none',
        'data-highlighted:bg-accent data-highlighted:text-accent-foreground',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        "text-sm md:text-xs [&_svg:not([class*='size-'])]:size-3.5 [&_svg]:pointer-events-none [&_svg]:shrink-0",
        className,
      )}
      {...props}
    >
      {children}
      <ComboboxPrimitive.ItemIndicator
        render={<span className="pointer-events-none absolute right-2 flex items-center justify-center" />}
      >
        <CheckIcon className="pointer-events-none size-4" />
      </ComboboxPrimitive.ItemIndicator>
    </ComboboxPrimitive.Item>
  );
};

const ComboboxGroup = ({ className, ...props }: ComboboxPrimitive.Group.Props) => {
  return <ComboboxPrimitive.Group data-slot="combobox-group" className={cn(className)} {...props} />;
};

const ComboboxGroupLabel = ({ className, ...props }: ComboboxPrimitive.GroupLabel.Props) => {
  return (
    <ComboboxPrimitive.GroupLabel
      data-slot="combobox-group-label"
      className={cn('px-2 py-1.5 font-semibold text-muted-foreground text-xs', className)}
      {...props}
    />
  );
};

const ComboboxCollection = ({ ...props }: ComboboxPrimitive.Collection.Props) => {
  return <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />;
};

// gloss
const ComboboxEmpty = ({ className, ...props }: ComboboxPrimitive.Empty.Props) => {
  return (
    <ComboboxPrimitive.Empty
      data-slot="combobox-empty"
      className={cn(
        'hidden w-full justify-center py-4 text-center text-muted-foreground text-sm group-data-empty/combobox-content:flex',
        'group-has-[[data-slot=combobox-list][data-empty]]/combobox-content:flex',
        className,
      )}
      {...props}
    />
  );
};

const ComboboxSeparator = ({ className, ...props }: ComboboxPrimitive.Separator.Props) => {
  return (
    <ComboboxPrimitive.Separator
      data-slot="combobox-separator"
      className={cn('-mx-1 my-1 h-px bg-border', className)}
      {...props}
    />
  );
};

const COMBOBOX_CHIPS_SIZES: Record<'sm' | 'default' | 'lg', string> = {
  sm: 'min-h-7 text-xs',
  default: '',
  lg: 'min-h-10',
};

const ComboboxChips = ({
  className,
  size = 'default',
  ...props
}: React.ComponentPropsWithRef<typeof ComboboxPrimitive.Chips> &
  ComboboxPrimitive.Chips.Props & { size?: 'sm' | 'default' | 'lg' }) => {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      className={cn(
        'border-input bg-field',
        'focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30',
        'has-aria-invalid:border-destructive has-aria-invalid:ring-destructive/20 dark:has-aria-invalid:ring-destructive/40',
        'flex min-h-9 flex-wrap items-center gap-1 rounded-lg border bg-clip-padding px-2 py-1 text-sm transition-colors md:text-xs',
        'has-data-[slot=combobox-chip]:px-1',
        COMBOBOX_CHIPS_SIZES[size],
        className,
      )}
      {...props}
    />
  );
};

const ComboboxChip = ({
  className,
  children,
  showRemove = true,
  ...props
}: ComboboxPrimitive.Chip.Props & { showRemove?: boolean }) => {
  return (
    <ComboboxPrimitive.Chip
      data-slot="combobox-chip"
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-secondary px-1.5 py-0.5 font-medium text-foreground text-xs',
        'has-data-[slot=combobox-chip-remove]:pr-0',
        'has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
      {showRemove && (
        <ComboboxPrimitive.ChipRemove
          render={<Button variant="ghost" size="icon-xs" />}
          className="-ml-1 opacity-50 hover:opacity-100"
          data-slot="combobox-chip-remove"
        >
          <XIcon className="pointer-events-none" />
        </ComboboxPrimitive.ChipRemove>
      )}
    </ComboboxPrimitive.Chip>
  );
};

// gloss
const ComboboxChipsInput = ({ className, ...props }: ComboboxPrimitive.Input.Props) => {
  return (
    <ComboboxPrimitive.Input
      data-slot="combobox-chips-input"
      className={cn(
        'min-w-16 flex-1 border-0 bg-transparent p-0 text-sm shadow-none outline-none focus:border-0 focus:shadow-none focus:ring-0 md:text-xs',
        className,
      )}
      {...props}
    />
  );
};

const ComboboxLoading = ({ className, children, ...props }: React.ComponentProps<'div'>) => {
  return (
    <div
      data-slot="combobox-loading"
      className={cn('flex items-center justify-center gap-2 px-2 py-4 text-muted-foreground text-sm', className)}
      {...props}
    >
      <Loader2 className="size-4 animate-spin" />
      {children}
    </div>
  );
};

const useComboboxAnchor = () => {
  return React.useRef<HTMLDivElement | null>(null);
};

export {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxClear,
  ComboboxCollection,
  ComboboxContent,
  ComboboxContentSlot,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxInlineContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxLoading,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
  useComboboxFilter,
};
